import { sessionFailureState } from "./session_policy.js";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import apiClient, { setSessionExpiredHandler } from "../api/client.js";

/**
 * Auth state for the HQ SPA.
 *
 * Tokens are httpOnly cookies set by the backend, so we never touch them in JS.
 * The only thing we keep in React state is the resolved user object.
 *
 * On mount we call /api/auth/me to bootstrap the session — if the access cookie
 * is expired, the axios interceptor transparently calls /refresh_token before
 * returning. If both fail, `user` stays null and routes guarded by
 * <RequireAuth/> bounce to /login.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | unauthed

  const [checkingSession, setCheckingSession] = useState(false);
  const sessionCheck = useRef(false);
  const bootstrap = useCallback(async () => {
    if (sessionCheck.current) return;
    sessionCheck.current = true;
    setCheckingSession(true);
    try {
      const { data } = await apiClient.get("/api/auth/me");
      setUser(data.user);
      setStatus("authed");
    } catch (error) {
      const next = sessionFailureState(error);
      if (next === "unauthed") setUser(null);
      setStatus(next);
    } finally {
      sessionCheck.current = false;
      setCheckingSession(false);
    }
  }, []);

  useEffect(() => {
    if (status !== "unavailable" || checkingSession) return;
    const retry = setTimeout(bootstrap, 5000);
    return () => clearTimeout(retry);
  }, [status, checkingSession, bootstrap]);

  // Let the axios interceptor drop us to unauthed when a token refresh fails
  // mid-session. RequireAuth then redirects to /login (preserving the path).
  // Registered before bootstrap so it's live for the very first request.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setStatus("unauthed");
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Step one. Resolves with the emailed-code challenge — it does NOT sign the
  // user in; only verifyOtp does. Exception: a development server skips the
  // code and signs in here, in which case this resolves with null.
  const login = useCallback(async (user_name, password) => {
    const { data } = await apiClient.post("/api/auth/login", {
      user_name,
      password,
    });
    if (!data.two_factor_required && data.user) {
      setUser(data.user);
      setStatus("authed");
      return null;
    }
    return data.challenge;
  }, []);

  // Step two. The pending cookie identifies the challenge; only the code is sent.
  const verifyOtp = useCallback(async (code) => {
    const { data } = await apiClient.post("/api/auth/verify_otp", { code });
    setUser(data.user);
    setStatus("authed");
    return data.user;
  }, []);

  const resendOtp = useCallback(async () => {
    const { data } = await apiClient.post("/api/auth/resend_otp", {});
    return data.challenge;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/api/auth/logout");
    } catch {
      // best-effort — clear local state regardless
    }
    setUser(null);
    setStatus("unauthed");
  }, []);

  const value = useMemo(
    () => ({ user, status, login, verifyOtp, resendOtp, logout, refresh: bootstrap, checkingSession }),
    [user, status, login, verifyOtp, resendOtp, logout, bootstrap, checkingSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
